import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { useFocusMode } from '../../../context/useFocusMode'
import { FocusModePicker } from './FocusModePicker'
import { FocusModeTimer } from './FocusModeTimer'
import { FocusModeCriticalToastStrip } from './FocusModeCriticalToastStrip'
import { OverflowTip } from '../Hover/OverflowTip'
import { Button } from '../Button'
import { IconButton } from '../IconButton'
import { Pressable } from '../Pressable'
import type { HelperAssignedItem } from '../../../services/helper/helperAssignedItemsService'
import { getChecklistBookName } from '../../../utils/checklistBooks'

const elapsedFor = (elapsedBeforePauseMs: number, sessionStartMs: number | null, paused: boolean) =>
  elapsedBeforePauseMs + (!paused && sessionStartMs !== null ? Math.max(0, Date.now() - sessionStartMs) : 0)

const byDueDateAsc = (a: HelperAssignedItem, b: HelperAssignedItem) => {
  const av = a.scheduleEndDate ?? a.endDate ?? '9999-12-31'
  const bv = b.scheduleEndDate ?? b.endDate ?? '9999-12-31'
  return av < bv ? -1 : av > bv ? 1 : 0
}

export const FocusModeOverlay = () => {
  const focusMode = useFocusMode()
  const [durationLocked, setDurationLocked] = useState(false)
  const [customMinutes, setCustomMinutes] = useState('')
  const [customOpen, setCustomOpen] = useState(false)
  const [addQueueOpen, setAddQueueOpen] = useState(false)
  const [queueDrawerOpen, setQueueDrawerOpen] = useState(false)
  const exitButtonRef = useRef<HTMLButtonElement | null>(null)
  const gestureRef = useRef<{ startY: number; lastY: number; startedAt: number; travelled: number } | null>(null)

  const elapsedMs = elapsedFor(focusMode.elapsedBeforePauseMs, focusMode.sessionStartMs, focusMode.paused)

  const exit = useCallback(() => {
    focusMode.exit()
  }, [focusMode])


  useEffect(() => {
    if (!focusMode.active) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resets session-local controls.
    setDurationLocked(false)
    setCustomMinutes('')
    setCustomOpen(false)
    exitButtonRef.current?.focus()
  }, [focusMode.active, focusMode.workItem?.id])

  useEffect(() => {
    if (!focusMode.active) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') exit()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [focusMode.active, exit])

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return
    gestureRef.current = { startY: event.clientY, lastY: event.clientY, startedAt: performance.now(), travelled: 0 }
  }

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = gestureRef.current
    if (!gesture) return
    gesture.lastY = event.clientY
    gesture.travelled = Math.max(gesture.travelled, event.clientY - gesture.startY)
  }

  const handlePointerUp = () => {
    const gesture = gestureRef.current
    gestureRef.current = null
    if (gesture && gesture.travelled >= 40 && performance.now() - gesture.startedAt < 400) exit()
  }

  const setDurationMinutes = (minutes: number) => {
    focusMode.setPlannedMinutes(minutes)
    setDurationLocked(true)
    setCustomOpen(false)
  }

  if (focusMode.pendingEnter) {
    return (
      <FocusModePicker
        open
        items={[...focusMode.taskFeedItems].sort(byDueDateAsc)}
        onPick={focusMode.enter}
        onOpenTodos={() => {
          focusMode.cancelPendingEnter()
          focusMode.openTodos()
        }}
        onDismiss={focusMode.cancelPendingEnter}
      />
    )
  }

  if (!focusMode.active || !focusMode.workItem) return null

  const showDurationChoices = !durationLocked && elapsedMs < 30_000
  const currentTaskNumber = focusMode.completedItems.length + 1
  const totalTaskCount = focusMode.completedItems.length + 1 + focusMode.queue.length
  const hasQueue = focusMode.queue.length > 0
  const isTimeUp = elapsedMs >= focusMode.timerConfig.plannedMs

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Focus mode"
      className="fixed inset-0 z-[60] flex min-h-[100svh] flex-col overflow-hidden bg-[var(--bg)] px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-[max(1.25rem,env(safe-area-inset-top))] text-[var(--text-strong)]"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => {
        gestureRef.current = null
      }}
    >
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[color-mix(in_srgb,var(--accent)_22%,transparent)] blur-[80px]" aria-hidden="true" />
      <header className="relative z-10 flex min-h-11 items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-black uppercase tracking-[0.22em] text-[var(--text)]">Focus</span>
          {totalTaskCount > 1 ? (
            <span className="rounded-full border border-[color-mix(in_srgb,var(--accent)_40%,transparent)] bg-[var(--accent-soft)] px-2.5 py-0.5 text-xs font-bold text-[var(--accent-strong)]">
              Task {currentTaskNumber} of {totalTaskCount}
            </span>
          ) : null}
        </div>
        <IconButton
          ref={exitButtonRef}
          variant="tertiary"
          size="lg"
          label="Exit Focus Mode"
          icon={<CloseRoundedIcon fontSize="small" />}
          onClick={exit}
        />
      </header>
      <div className="relative z-10 min-h-0"><FocusModeCriticalToastStrip /></div>
      <main className="relative z-10 flex flex-1 flex-col items-center justify-center gap-5 py-2">
        {showDurationChoices ? (
          <div className="flex flex-wrap justify-center gap-2" aria-label="Focus duration">
            {[25, 45, 60].map((minutes) => (
              <Button
                key={minutes}
                variant="tertiary"
                size="md"
                onClick={() => setDurationMinutes(minutes)}
              >
                {minutes}m
              </Button>
            ))}
            <Button
              variant="tertiary"
              size="md"
              pressed={customOpen}
              onClick={() => setCustomOpen((open) => !open)}
            >
              Custom
            </Button>
            {customOpen ? (
              <form
                className="flex gap-2"
                onSubmit={(event) => {
                  event.preventDefault()
                  const minutes = Number(customMinutes)
                  if (Number.isFinite(minutes) && minutes > 0) setDurationMinutes(minutes)
                }}
              >
                <input
                  aria-label="Custom focus minutes"
                  className="w-20 rounded-full border border-[var(--border-strong)] bg-[var(--surface)] px-3 text-sm text-[var(--text-strong)]"
                  inputMode="numeric"
                  min="1"
                  type="number"
                  value={customMinutes}
                  onChange={(event) => setCustomMinutes(event.target.value)}
                />
                <Button type="submit" variant="primary" size="md">
                  Set
                </Button>
              </form>
            ) : null}
          </div>
        ) : null}
        <FocusModeTimer
          plannedMs={focusMode.timerConfig.plannedMs}
          sessionStartMs={focusMode.sessionStartMs}
          elapsedBeforePauseMs={focusMode.elapsedBeforePauseMs}
          paused={focusMode.paused}
          onPause={focusMode.pause}
          onResume={focusMode.resume}
          onAddMinutes={focusMode.addMinutes}
          onRestart={focusMode.restart}
        />
        <article className="w-full max-w-md rounded-3xl border border-[var(--border)] bg-[var(--surface-card)] p-5 shadow-2xl backdrop-blur">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[var(--accent-strong)]">
            {getChecklistBookName(focusMode.workItem)} · {focusMode.workItem.effectiveStatus}
          </p>
          <h1 className="mt-2 text-xl font-black text-[var(--text-strong)]">{focusMode.workItem.title}</h1>
          <p className="mt-3 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-xs font-semibold text-[var(--text)]">
            Readiness — ready to work
          </p>
        </article>

        {/* Multi-task queue controls */}
        <div className="flex w-full max-w-md flex-col gap-2">
          {hasQueue ? (
            <Button
              variant={isTimeUp ? 'primary' : 'secondary'}
              size="lg"
              className={`w-full${isTimeUp ? ' motion-safe:animate-pulse' : ''}`}
              onClick={focusMode.nextTask}
              trailingIcon={<span>→</span>}
            >
              Next Task ({focusMode.queue.length} remaining)
            </Button>
          ) : null}

          <div className="flex items-center justify-between px-1 text-xs">
            {hasQueue ? (
              <Button
                variant="link"
                size="sm"
                onClick={() => setQueueDrawerOpen((prev) => !prev)}
              >
                {queueDrawerOpen ? 'Hide Queue ▲' : `View Queue (${focusMode.queue.length}) ▼`}
              </Button>
            ) : (
              <span className="text-[var(--muted)] text-[11px]">No more tasks in queue</span>
            )}
            <Button
              variant="link"
              size="sm"
              onClick={() => setAddQueueOpen(true)}
            >
              + Add to queue
            </Button>
          </div>

          {hasQueue && queueDrawerOpen ? (
            <div className="max-h-32 w-full space-y-1.5 overflow-y-auto rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-2.5">
              {focusMode.queue.map((item, idx) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-2 rounded-xl bg-[var(--surface-card)] px-3 py-1.5 text-xs"
                >
                  <OverflowTip label={`${idx + 1}. ${item.title}`}>
                    <div className="min-w-0 flex-1 truncate">
                      <span className="mr-2 font-mono text-[var(--muted)]">{idx + 1}.</span>
                      <span className="font-semibold text-[var(--text)]">{item.title}</span>
                    </div>
                  </OverflowTip>
                  <Pressable
                    className="p-1 text-[var(--muted)] hover:text-[var(--danger-text)]"
                    onClick={() => focusMode.removeFromQueue(item.id)}
                    aria-label={`Remove ${item.title} from queue`}
                  >
                    ✕
                  </Pressable>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </main>

      {/* TODO(focus-mode-phase-2): wire to TaskCarousel action primitives. */}
      <nav className="relative z-10 grid grid-cols-3 gap-2" aria-label="Focus actions">
        {['Proof', 'Location', 'Blocker'].map((label) => (
          <Button
            key={label}
            variant="tertiary"
            size="lg"
            disabled
            className="w-full"
          >
            {label}
          </Button>
        ))}
      </nav>

      {addQueueOpen ? (
        <FocusModePicker
          open
          title="Add to queue"
          actionLabel="Add to Queue"
          excludeIds={[
            ...(focusMode.workItem ? [focusMode.workItem.id] : []),
            ...focusMode.queue.map((q) => q.id),
          ]}
          items={[...focusMode.taskFeedItems].sort(byDueDateAsc)}
          onPick={(selected) => {
            focusMode.addToQueue(selected)
            setAddQueueOpen(false)
          }}
          onDismiss={() => setAddQueueOpen(false)}
        />
      ) : null}
    </div>
  )
}
